import type { UserDetail } from "./userManagement.queries.types";
import type {
  UserAccountType,
  UserListApiRow,
  UserListRow,
  UserRecord,
  UsersDirectoryClient,
  UserStatus,
} from "./usersDirectory.types";

const text = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const names = (value: string[] | null | undefined): string[] =>
  Array.isArray(value) ? value.filter((name): name is string => Boolean(text(name))) : [];

/**
 * `users.status` holds `active` / `inactive` and nothing else; an invite has no state of its own
 * in the database (Cognito holds it). An unknown string is read as unknown rather than guessed
 * into one of the three the list draws.
 */
export const userStatusOf = (value: unknown): UserStatus | null => {
  switch (text(value)?.toLowerCase()) {
    case "active":
      return "active";
    case "invited":
    case "pending":
      return "invited";
    case "inactive":
    case "deactivated":
      return "deactivated";
    default:
      return null;
  }
};

/** Ids arrive as uuid strings today, and as numbers on the older seeded rows. */
const id = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value).trim();

/**
 * The two shapes share no key for the person's roles or their entity, so each is read from
 * whichever the response used. At ServEngine level the flat `roles` array says nothing about
 * which of them are temporary, so they all count as permanent there.
 */
export const normalizeListUser = (row: UserListApiRow): UserListRow => {
  const rowId = id(row.id);
  // The ServEngine-level list keys its rows by user *and* entity, so the person's own id is a
  // separate field there; inside an entity the row id is the person.
  const userId = id(row.userId) || rowId;
  return {
    rowId,
    userId,
    name: text(row.username) ?? "",
    email: text(row.email),
    permanentRoles: row.role ? names(row.role.permanent) : names(row.roles),
    temporaryRoles: row.role ? names(row.role.temporary) : [],
    entity: text(row.entityName) ?? text(row.assignedTo),
    entityId: text(row.entityId),
    status: userStatusOf(row.status),
    lastSignInAt: text(row.lastSignInAt),
  };
};

const accountTypeOf = (value: unknown): UserAccountType | null =>
  value === "entity" || value === "servengine" ? value : null;

/**
 * SERV-3578 (UI30): the person read answers status, last sign-in, account type, login entity,
 * password date and deactivation; nothing records the password date or a deactivation yet, so
 * those come back null and the record says "Not available yet". No read names the inviter.
 */
export const normalizeUserDetail = (detail: UserDetail): UserRecord => ({
  ...detail,
  status: userStatusOf(detail.status),
  accountType: accountTypeOf(detail.accountType),
  lastSignInAt: text(detail.lastSignInAt),
  invitedAt: text(detail.createdAt),
  invitedBy: null,
  loginEntityId: text(detail.loginEntityId),
  passwordChangedAt: text(detail.passwordChangedAt),
  deactivatedAt: text(detail.deactivatedAt),
  deactivatedBy: text(detail.deactivatedBy),
});

export const liveUsersDirectoryClient: UsersDirectoryClient = {
  decorateListUser: normalizeListUser,
  decorateUserDetail: normalizeUserDetail,
};
